import { useQuery } from '@tanstack/react-query'
import { ArrowDownToLine, ArrowUpFromLine, CircleAlert, CornerDownLeft, RadioTower, Search as SearchIcon, SearchX, User, type LucideIcon } from 'lucide-react'
import { type KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { searchEverything, type SearchHit } from '../../api/admin'
import { StatusPill, describeError, when } from '../ui/Ui'
import styles from './Search.module.css'

const KIND_RU: Record<SearchHit['kind'], string> = { user: 'пользователь', withdrawal: 'вывод', deposit: 'депозит', signal: 'сигнал' }
const PATH_OF: Record<SearchHit['kind'], string> = { user: '/users', withdrawal: '/withdrawals', deposit: '/users', signal: '/signals' }
const KIND_ICON: Record<SearchHit['kind'], LucideIcon> = { user: User, withdrawal: ArrowUpFromLine, deposit: ArrowDownToLine, signal: RadioTower }

/** A screen the palette can jump to (the sidebar's destinations this desk may open). */
export interface SearchDestination {
  to: string
  label: string
  group: string
  icon: LucideIcon
}

type Entry = { type: 'nav'; key: string; destination: SearchDestination } | { type: 'hit'; key: string; hit: SearchHit }

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
const MIN_QUERY = 3
const NO_HITS: SearchHit[] = []

function fold(text: string): string {
  return text.toLocaleLowerCase('ru-RU').replace(/ё/g, 'е')
}

function charactersLeft(count: number): string {
  return count === 1 ? 'ещё 1 символ' : `ещё ${count} символа`
}

/**
 * One box for whatever the person pasted: an email, an id, a hash, an
 * address, a signal code — opened from the top bar or with ⌘K / Ctrl+K.
 * Results open the screen the hit belongs to with the email pre-searched, so
 * the operator lands on the row, not the list. Typing a screen's name jumps
 * to it; ↑/↓ pick, Enter opens, Esc closes.
 */
export function GlobalSearch({ destinations = [] }: { destinations?: readonly SearchDestination[] }): JSX.Element {
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [active, setActive] = useState(0)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listId = useId()

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(value.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [value])

  const results = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchEverything(query),
    enabled: open && query.length >= MIN_QUERY,
  })

  const close = useCallback((): void => {
    if (!open || closing) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setOpen(false)
      return
    }
    setClosing(true)
    window.setTimeout(() => {
      setClosing(false)
      setOpen(false)
    }, 120)
  }, [open, closing])

  // ⌘K / Ctrl+K anywhere opens (or closes) the palette — unless another dialog is up.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'k') return
      const otherDialog = Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== dialogRef.current)
      if (otherDialog) return
      event.preventDefault()
      if (open) close()
      else setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  // A modal <dialog>: the browser keeps focus inside and the page inert.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    if (!open && dialog.open) {
      dialog.close()
      triggerRef.current?.focus({ preventScroll: true })
    }
  }, [open])

  const trimmed = value.trim()
  const wantsSearch = trimmed.length >= MIN_QUERY

  const navEntries = useMemo<Entry[]>(() => {
    const needle = fold(trimmed)
    const matches = needle === '' ? destinations : destinations.filter((d) => fold(d.label).includes(needle) || fold(d.group).includes(needle))
    return matches.slice(0, needle === '' ? destinations.length : 5).map((destination) => ({ type: 'nav', key: `nav-${destination.to}`, destination }))
  }, [destinations, trimmed])

  const settled = query === trimmed
  const loading = wantsSearch && (!settled || results.isPending)
  const hits = wantsSearch && settled && results.data ? results.data.items : NO_HITS
  const hitEntries = useMemo<Entry[]>(() => hits.map((hit) => ({ type: 'hit', key: `${hit.kind}-${hit.id}`, hit })), [hits])
  const entries = useMemo(() => [...hitEntries, ...navEntries], [hitEntries, navEntries])

  useEffect(() => setActive(0), [trimmed, hits])

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const go = (hit: SearchHit): void => {
    close()
    const email = hit.userEmail ?? (hit.kind === 'user' ? hit.title : null)
    navigate(email !== null && hit.kind !== 'signal' ? `${PATH_OF[hit.kind]}?search=${encodeURIComponent(email)}` : PATH_OF[hit.kind])
  }

  const choose = (entry: Entry | undefined): void => {
    if (!entry) return
    if (entry.type === 'hit') {
      go(entry.hit)
      return
    }
    close()
    navigate(entry.destination.to)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => (entries.length === 0 ? 0 : (index + 1) % entries.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => (entries.length === 0 ? 0 : (index - 1 + entries.length) % entries.length))
    } else if ((event.key === 'Home' || event.key === 'End') && (event.ctrlKey || event.metaKey || value === '')) {
      // Home/End jump through the list; in a typed query they keep moving the caret unless ⌘/Ctrl is held.
      event.preventDefault()
      setActive(event.key === 'Home' || entries.length === 0 ? 0 : entries.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(entries[active])
    }
  }

  const optionId = (index: number): string => `${listId}-option-${index}`

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label="Поиск по всему"
        aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
      >
        <SearchIcon size={15} strokeWidth={2} aria-hidden="true" className={styles.triggerIcon} />
        <span className={styles.triggerText}>{trimmed !== '' ? trimmed : 'Найти: почта, id, хеш, адрес, код'}</span>
        <span className={styles.triggerKeys} aria-hidden="true">
          <kbd className="kbd">{IS_MAC ? '⌘' : 'Ctrl'}</kbd>
          <kbd className="kbd">K</kbd>
        </span>
      </button>

      <dialog
        ref={dialogRef}
        className={[styles.palette, closing ? styles.closing : ''].filter(Boolean).join(' ')}
        aria-label="Поиск"
        onCancel={(event) => {
          event.preventDefault()
          close()
        }}
        onMouseDown={(event) => {
          // A press on the backdrop (the dialog box itself, outside the panel) closes.
          if (event.target === dialogRef.current) close()
        }}
      >
        {open ? (
          <div className={styles.panel}>
            <div className={styles.inputRow}>
              <SearchIcon size={18} strokeWidth={2} aria-hidden="true" className={styles.inputIcon} />
              <input
                ref={inputRef}
                className={styles.input}
                placeholder="Почта, id, хеш, адрес, код сигнала или раздел"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={onKeyDown}
                role="combobox"
                aria-label="Поиск по всему"
                aria-expanded={entries.length > 0}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={entries[active] ? optionId(active) : undefined}
                autoComplete="off"
                spellCheck={false}
              />
              {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
              <button type="button" className={styles.esc} onClick={close} aria-label="Закрыть поиск">
                <kbd className="kbd">Esc</kbd>
              </button>
            </div>

            <div ref={listRef} id={listId} role="listbox" aria-label="Результаты" className={styles.list}>
              {wantsSearch ? (
                <div className={styles.group} role="presentation">
                  <div className={styles.groupTitle} role="presentation">
                    Найдено
                    {hits.length > 0 ? <span className={styles.groupCount}>{hits.length}</span> : null}
                  </div>
                  {loading ? (
                    <div className={styles.skeletons} aria-hidden="true">
                      {[0, 1, 2].map((row) => (
                        <div key={row} className={styles.skeletonRow}>
                          <span className={`skeleton ${styles.skeletonIcon}`} />
                          <span className={styles.skeletonText}>
                            <span className="skeleton" style={{ width: `${56 - row * 12}%`, height: 10 }} />
                            <span className="skeleton" style={{ width: `${34 + row * 8}%`, height: 8 }} />
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : results.isError ? (
                    <div className={`${styles.note} ${styles.noteError}`} role="alert">
                      <CircleAlert size={16} aria-hidden="true" />
                      {describeError(results.error)}
                    </div>
                  ) : hits.length === 0 ? (
                    <div className={styles.note}>
                      <SearchX size={16} aria-hidden="true" />
                      Ничего не нашлось по «{trimmed}»
                    </div>
                  ) : (
                    <div className={styles.rows}>
                      {hitEntries.map((entry, index) =>
                        entry.type === 'hit' ? <HitRow key={entry.key} id={optionId(index)} hit={entry.hit} active={index === active} onHover={() => setActive(index)} onChoose={() => go(entry.hit)} /> : null,
                      )}
                    </div>
                  )}
                </div>
              ) : trimmed.length > 0 ? (
                <div className={styles.hint}>Введите {charactersLeft(MIN_QUERY - trimmed.length)} — и поиск пройдёт по почте, id, хешу, адресу и коду сигнала.</div>
              ) : null}

              {navEntries.length > 0 ? (
                <div className={styles.group} role="presentation">
                  <div className={styles.groupTitle} role="presentation">
                    Разделы
                  </div>
                  {navEntries.map((entry, offset) => {
                    if (entry.type !== 'nav') return null
                    const index = hitEntries.length + offset
                    const Icon = entry.destination.icon
                    return (
                      <div
                        key={entry.key}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === active}
                        data-active={index === active}
                        className={styles.option}
                        onMouseMove={() => index !== active && setActive(index)}
                        onClick={() => choose(entry)}
                      >
                        <span className={styles.optionIcon}>
                          <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
                        </span>
                        <span className={styles.optionMain}>
                          <span className={styles.optionTitle}>{entry.destination.label}</span>
                        </span>
                        <span className={styles.optionMeta}>{entry.destination.group}</span>
                        <CornerDownLeft size={14} aria-hidden="true" className={styles.enter} />
                      </div>
                    )
                  })}
                </div>
              ) : wantsSearch ? null : (
                <div className={styles.hint}>Нет разделов с таким названием.</div>
              )}
            </div>

            <div className={styles.footer} aria-hidden="true">
              <span>
                <kbd className="kbd">↑</kbd>
                <kbd className="kbd">↓</kbd> выбрать
              </span>
              <span>
                <kbd className="kbd">↵</kbd> открыть
              </span>
              <span>
                <kbd className="kbd">Esc</kbd> закрыть
              </span>
              <span className={styles.footerHint}>от {MIN_QUERY} символов — поиск по всей базе</span>
            </div>
          </div>
        ) : null}
      </dialog>
    </>
  )
}

function HitRow({ id, hit, active, onHover, onChoose }: { id: string; hit: SearchHit; active: boolean; onHover: () => void; onChoose: () => void }): JSX.Element {
  const Icon = KIND_ICON[hit.kind]
  const secondary = hit.userEmail ?? hit.subtitle
  return (
    <div id={id} role="option" aria-selected={active} data-active={active} className={styles.option} onMouseMove={() => !active && onHover()} onClick={onChoose}>
      <span className={styles.optionIcon} data-kind={hit.kind}>
        <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
      </span>
      <span className={styles.optionMain}>
        <span className={styles.optionTitle}>{hit.title}</span>
        <span className={styles.optionSub}>
          <span className={styles.kind}>{KIND_RU[hit.kind]}</span>
          {secondary ? <span className={styles.sub}>{secondary}</span> : null}
        </span>
      </span>
      {hit.status ? <StatusPill status={hit.status} /> : null}
      <span className={styles.optionWhen}>{when(hit.createdAt)}</span>
      <CornerDownLeft size={14} aria-hidden="true" className={styles.enter} />
    </div>
  )
}
